import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const pin = (bg, inner) =>
  L.divIcon({
    className: "bg-transparent",
    html: `<div style="width:34px;height:34px;border-radius:50% 50% 50% 0;background:${bg};transform:rotate(-45deg);box-shadow:0 3px 8px rgba(0,0,0,.3);display:flex;align-items:center;justify-content:center;border:2px solid #fff">
      <div style="transform:rotate(45deg);color:#fff;font-size:15px;line-height:1">${inner}</div></div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 32],
  });

const riderIcon = () =>
  L.divIcon({
    className: "bg-transparent",
    html: `<div style="position:relative;width:38px;height:38px">
      <span style="position:absolute;inset:0;border-radius:50%;background:rgba(255,107,53,.28);animation:bgfPulse 1.6s ease-out infinite"></span>
      <div style="position:absolute;top:3px;left:3px;width:32px;height:32px;border-radius:50%;background:#FF6B35;border:3px solid #fff;box-shadow:0 3px 8px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;color:#fff;font-size:16px">🛵</div>
    </div>
    <style>@keyframes bgfPulse{0%{transform:scale(.6);opacity:.9}100%{transform:scale(1.8);opacity:0}}</style>`,
    iconSize: [38, 38],
    iconAnchor: [19, 19],
  });

const RESTAURANT_ICON = pin("#1A1A1D", "🍽️");
const CUSTOMER_ICON = pin("#16A34A", "🏠");

const haversine = (a, b) => {
  const R = 6371, r = (d) => (d * Math.PI) / 180;
  const dLat = r(b.lat - a.lat), dLng = r(b.lng - a.lng);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(r(a.lat)) * Math.cos(r(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
};

export const LiveTrackMap = ({ restaurant, customer, rider, phase, onRoute }) => {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const layers = useRef({});

  useEffect(() => {
    if (mapRef.current || !elRef.current) return;
    const m = L.map(elRef.current, { zoomControl: false, attributionControl: true, scrollWheelZoom: false });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19, attribution: "© OpenStreetMap",
    }).addTo(m);
    m.setView([restaurant?.lat || customer?.lat || 17.4, restaurant?.lng || customer?.lng || 78.4], 13);
    mapRef.current = m;
    return () => { m.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    const m = mapRef.current;
    if (!m || !customer?.lat) return;
    const useRider = phase === "delivery" && rider?.lat;
    const origin = useRider ? rider : restaurant;

    const set = (key, latlng, icon) => {
      if (!latlng?.lat) return;
      if (layers.current[key]) layers.current[key].setLatLng([latlng.lat, latlng.lng]);
      else layers.current[key] = L.marker([latlng.lat, latlng.lng], { icon }).addTo(m);
    };
    set("customer", customer, CUSTOMER_ICON);
    if (restaurant?.lat) set("restaurant", restaurant, RESTAURANT_ICON);
    if (useRider) set("rider", rider, riderIcon());
    else if (layers.current.rider) { m.removeLayer(layers.current.rider); delete layers.current.rider; }

    const pts = [customer, restaurant, useRider ? rider : null].filter((p) => p?.lat);
    if (pts.length) m.fitBounds(pts.map((p) => [p.lat, p.lng]), { padding: [40, 40], maxZoom: 15 });

    if (!origin?.lat) return;
    let cancelled = false;
    const drawStraight = () => {
      const d = haversine(origin, customer);
      if (layers.current.route) m.removeLayer(layers.current.route);
      layers.current.route = L.polyline([[origin.lat, origin.lng], [customer.lat, customer.lng]],
        { color: "#FF6B35", weight: 4, opacity: 0.8, dashArray: "6 8" }).addTo(m);
      onRoute?.({ distanceKm: d, etaMin: Math.max(3, Math.round((d / 22) * 60)) });
    };
    fetch(`https://router.project-osrm.org/route/v1/driving/${origin.lng},${origin.lat};${customer.lng},${customer.lat}?overview=full&geometries=geojson`)
      .then((r) => r.json())
      .then((j) => {
        if (cancelled) return;
        const route = j.routes?.[0];
        if (!route) return drawStraight();
        const coords = route.geometry.coordinates.map(([lng, lat]) => [lat, lng]);
        if (layers.current.route) m.removeLayer(layers.current.route);
        layers.current.route = L.polyline(coords, { color: "#FF6B35", weight: 5, opacity: 0.85 }).addTo(m);
        onRoute?.({ distanceKm: route.distance / 1000, etaMin: Math.max(3, Math.round(route.duration / 60)) });
      })
      .catch(() => !cancelled && drawStraight());
    return () => { cancelled = true; };
  }, [restaurant?.lat, restaurant?.lng, customer?.lat, customer?.lng, rider?.lat, rider?.lng, phase]);

  return <div ref={elRef} className="w-full h-[260px]" data-testid="live-track-map" />;
};
